import { gql } from '@apollo/client';

const GET_STUDY_GROUP_WITHOUT_USERS = gql`
  query StudyGroupWithoutUsers($studyGroupId: ID!, $userId: String!) {
    studyGroup(pk: $studyGroupId) {
      id
      pk
      name
      isSelfLearning
      isExplore
      trackAssignments
      trackChoice
      license {
        id
        period {
          lastDate
        }
        program {
          id
          pk
        }
      }
      tracksLicense {
        id
        name
      }
      shares {
        id
        contentId
      }
      chalkups(filters: { user: { id: $userId } }) {
        id
        contentId
      }
    }
    pinnings(filters: { user: { id: $userId } }) {
      id
      contentId
    }
  }
`;

const GET_STUDY_GROUP = gql`
  query StudyGroup($studyGroupId: ID!, $userId: String!) {
    studyGroup(pk: $studyGroupId) {
      id
      pk
      name
      isSelfLearning
      isExplore
      trackAssignments
      trackChoice
      license {
        id
        period {
          lastDate
        }
        program {
          id
          pk
        }
      }
      tracksLicense {
        id
        name
      }
      students {
        pk
        username
        email
        firstName
        lastName
        fullName
      }
      teachers {
        pk
        username
        email
        firstName
        lastName
        fullName
      }
      shares {
        id
        contentId
      }
      chalkups(filters: { user: { id: $userId } }) {
        id
        contentId
      }
    }
    pinnings(filters: { user: { id: $userId } }) {
      id
      contentId
    }
  }
`;

const GET_PROGRAM = gql`
  query Program($programId: ID!) {
    program(pk: $programId) {
      id
      pk
      name
      description
      videoUrl
      newMenu
      image {
        name
      }
      thumbnails
      guides {
        id
        name
        pdfUrl
        mediaType
        youtubeUrl
        preziUrl
        driveUrl
        fileUrl
        videoUrl
      }
    }
  }
`;

const GET_PROGRAM_AND_PROGRAM_UNITS = gql`
  fragment ClassPageUnitFragment on Unit {
    isPublished
    description
    image {
      name
    }
    thumbnails
    color
  }
  
  query ProgramAndProgramUnits($programId: ID!, $classPage: Boolean!) {
    program(pk: $programId) {
      id
      pk
      name
      description
      progress
      videoUrl
      newMenu
      image {
        name
      }
      thumbnails
      guides {
        id
        name
        pdfUrl
        mediaType
        youtubeUrl
        preziUrl
        driveUrl
        fileUrl
        videoUrl
      }
      programUnits {
        id
        progress
        name
        description
        thumbnails
        ...ClassPageUnitFragment @include(if: $classPage)
        modules {
          id
          name
          contents {
            id
            name
          }
        }
      }
    }
  }
`;

const GET_PROGRAM_STUDY_GROUPS = gql`
  query ProgramStudyGroups($programIdStr: String!) {
    studyGroups(filters: { license: { program: { id: $programIdStr } } }) {
      id
      name
      schoolName
      trackChoice
      trackAssignments
      tracks {
        id
        name
        tags
      }
    }
  }
`;

const GET_PROGRAM_STUDY_GROUP = gql`
  query ProgramStudyGroup($programIdStr: String!, $studyGroupId: String!) {
    studyGroups(
      filters: {
        license: { program: { id: $programIdStr } }
        id: $studyGroupId
      }
    ) {
      id
      name
      schoolName
      trackChoice
      trackAssignments
    }
  }
`;

const GET_TRACK = gql`
  fragment ClassPageUnitFragment on Unit {
    isPublished
    description
    image {
      name
    }
    thumbnails
    color
  }

  query Track($trackId: ID!, $classPage: Boolean!) {
    track(pk: $trackId) {
      id
      name
      progress @include(if: $classPage) @client
      conditionEnabled
      conditionShown
      trackGuides(filters: { enabled: true }) {
        id
        guide {
          id
          name
          pdfUrl
          mediaType
          youtubeUrl
          preziUrl
          driveUrl
          fileUrl
          videoUrl
          isDemo
          sortOrder
        }
      }
      trackUnits(filters: { enabled: true }) {
        id
        unit {
          id
          name
          isDemo
          ...ClassPageUnitFragment @include(if: $classPage)
        }
        progress @include(if: $classPage) @client
        canBeShared @include(if: $classPage) @client
        conditionEnabled
        conditionShown
        trackModules(filters: { enabled: true }) @skip(if: $classPage) {
          id
          conditionEnabled
          conditionShown
          module {
            id
            name
          }
          trackContents(filters: { enabled: true }) {
            id
            conditionEnabled
            conditionShown
            content {
              id
              name
              mediaType
              isShareable
              isDownloadable
              isDemo
              fileUrl
            }
            forwardContent {
              id
              name
              mediaType
              isShareable
              isDownloadable
              isDemo
              fileUrl
            }
          }
        }
      }
    }
  }
`;

const GET_TRACK_PROGRESS = gql`
  query TrackProgress($trackId: ID!, $trackIdString: String!) {
    track(pk: $trackId) {
      id
      progress
    }
    trackUnits(filters: { track: { id: $trackIdString }, enabled: true }) {
      id
      progress
      canBeShared
    }
  }
`;

const CREATE_CHALKUP = gql`
  mutation CompleteChalkup($contentId: String!, $studyGroupId: String) {
    completeContent(contentId: $contentId, studyGroupId: $studyGroupId) {
      id
      contentId
    }
  }
`;

const CREATE_SHARE = gql`
  mutation CompleteShare($contentId: String!, $studyGroupId: String!) {
    shareContent(contentId: $contentId, studyGroupId: $studyGroupId) {
      id
      contentId
    }
  }
`;

const CREATE_PINNING = gql`
  mutation CompletePinning($contentId: String!, $studyGroupId: String!) {
    pinContent(contentId: $contentId, studyGroupId: $studyGroupId) {
      id
      contentId
    }
  }
`;

const DELETE_CHALKUP = gql`
  mutation UnChalkUp($chalkUpId: String!) {
    deleteChalkUps(filters: { id: $chalkUpId }) {
      id
    }
  }
`;

const DELETE_SHARE = gql`
  mutation UnShare($shareId: String!) {
    deleteShares(filters: { id: $shareId }) {
      id
    }
  }
`;

const DELETE_PINNING = gql`
  mutation UnPin($pinId: String!) {
    deletePinnings(filters: { id: $pinId }) {
      id
    }
  }
`;

const CHOOSE_TRACK = gql`
  mutation ChooseTrack($trackId: String!, $studyGroupId: String!) {
    chooseTrack(trackId: $trackId, studyGroupId: $studyGroupId) {
      id
      track {
        id
      }
      studyGroup {
        id
      }
    }
  }
`;

const STUDENTS_TRACK_ASSIGNMENTS = gql`
  query StudentsTrackAssignemnts($studyGroupId: String!) {
    trackTeacherAssignments(filters: { studyGroup: { id: $studyGroupId } }) {
      id
      track {
        id
      }
      user {
        id
      }
    }
  }
`;

const ASSIGN_TRACK_TO_STUDENT = gql`
  mutation AssignTrackToStudent($data: AssignTrackToStudentInput!) {
    assignTrackToStudent(data: $data) {
      id
      track {
        id
      }
      user {
        id
      }
    }
  }
`;

const DELETE_TRACK_ASSIGNMENT = gql`
  mutation DeleteTrackAssignment($trackAssignmentId: String!) {
    deleteTrackTeacherAssignments(filters: { id: $trackAssignmentId }) {
      id
    }
  }
`;

const QUERY_CAN_BE_SHARED = gql`
  query CanBeShared($trackUnitId: ID!) {
    trackUnit(pk: $trackUnitId) {
      id
      canBeShared
    }
  }
`;

const USER_CHALKUPS_WITHOUT_CLASS = gql`
  query UserChalkupsWithoutClass($userId: String!) {
    chalkUps(filters: { studyGroup: null, user: { id: $userId } }) {
      id
      contentId
    }
  }
`;

export {
  GET_PROGRAM,
  GET_PROGRAM_AND_PROGRAM_UNITS,
  GET_PROGRAM_STUDY_GROUP,
  GET_PROGRAM_STUDY_GROUPS,
  GET_STUDY_GROUP_WITHOUT_USERS,
  GET_STUDY_GROUP,
  GET_TRACK,
  GET_TRACK_PROGRESS,
  QUERY_CAN_BE_SHARED,
  CREATE_CHALKUP,
  CREATE_SHARE,
  CREATE_PINNING,
  DELETE_CHALKUP,
  DELETE_SHARE,
  DELETE_PINNING,
  CHOOSE_TRACK,
  STUDENTS_TRACK_ASSIGNMENTS,
  ASSIGN_TRACK_TO_STUDENT,
  DELETE_TRACK_ASSIGNMENT,
  USER_CHALKUPS_WITHOUT_CLASS,
};
